<template>
  <div class="relative" :class="textfieldClasses">
    <input
      :id="id"
      :ref="refName"
      v-model="content"
      :class="`text-field-input ${hasIcon ? 'pr-56' : 'pr-16'}`"
      :type="type"
      :aria-label="ariaLabel"
      :aria-invalid="hasError"
      :aria-describedby="ariaDescribedByName"
      :required="required"
      :disabled="disabled"
      :readonly="readonly"
      :pattern="pattern"
      :maxlength="maxlength"
      :value="content"
      :inputmode="inputmode"
      :placeholder="placeholder"
      :name="name"
      :autocomplete="autocomplete"
      @keydown.enter.prevent
    />
    <label :class="'pointer-events-none ' + textfieldLabelFilled" :for="id">{{ labelName }}</label>
    <div v-if="hasIcon" class="text-field-icon">
      <slot v-if="(!hasError && !disabled) || iconStatic" name="icon" />
    </div>

    <div v-if="helperText" :id="ariaDescribedByName" class="mt-4 flex relative">
      <ErrorIcon v-if="hasError" class="mr-4 mt-4" />
      <SuccessIcon v-if="isValid" class="mr-4 mt-4" />
      <p class="text-field-helper" v-html="helperText">{{ helperText }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import ErrorIcon from '../../icons/ErrorIcon.vue';
import SuccessIcon from '../../icons/SuccessIcon.vue';
import helperStatus from '../../helpers/status';

export default Vue.extend({
  name: 'TextField',
  components: {
    ErrorIcon,
    SuccessIcon,
  },
  props: {
    labelName: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    ariaLabel: {
      type: String,
      required: false,
    },
    autocomplete: {
      type: String,
      required: false,
    },
    id: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    type: {
      type: String,
      required: true,
      validator(value: string) {
        return ['text', 'number', 'email', 'tel', 'search', 'password'].indexOf(value) !== -1;
      },
    },
    name: {
      type: String,
      default: '',
      required: false,
    },
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
    value: {
      type: String,
      default: '',
      required: false,
    },
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    required: {
      type: Boolean,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    inputmode: {
      type: String,
      required: false,
    },
    maxlength: {
      type: String,
      required: false,
    },
    pattern: {
      type: String,
      required: false,
    },
    limitInputToPattern: {
      type: Boolean,
      required: false,
    },
    helperText: {
      type: String,
      required: false,
    },
    iconStatic: {
      type: Boolean,
      default: false,
      required: false,
    },
    refName: {
      type: String,
      default: null,
      required: false,
    },
  },
  data(): any {
    return {
      content: this.value,
    };
  },
  computed: {
    ariaDescribedByName(): String | null {
      if (!this.helperText) {
        return null;
      }
      return `${this.id}-helper`;
    },
    textfieldClasses(): String {
      if (this.hasError) {
        return 'text-field-error';
      }
      if (this.isValid) {
        return 'text-field-valid';
      }
      return 'text-field';
    },
    textfieldLabelFilled(): String {
      if (this.content) {
        return 'text-field-label-filled';
      }
      return 'text-field-label';
    },
    hasError(): Boolean {
      return this.status === helperStatus.Error;
    },
    isValid(): Boolean {
      return this.status === helperStatus.Valid;
    },
    hasIcon() {
      return this.$slots.icon;
    },
  },
  watch: {
    content(selectedValue: string) {
      if (this.limitInputToPattern && this.pattern && new RegExp(this.pattern, 'g').test(selectedValue)) {
        this.content = this.content.match(this.pattern).join('');
      }
      this.$emit('input', this.content);
    },

    value(valueFromParent: String) {
      if (valueFromParent !== this.content) {
        this.content = valueFromParent;
      }
    },
  },
});
</script>
